import { useEffect, useMemo, useState } from "react";
import { getAddress, parseAbiItem, type Address, type Hash } from "viem";
import { baseClient, ethereumClient } from "./chain";
import { HARDWARE_COLLECTION, burnerAbi, hardwareCollectionAbi, receiverAbi } from "./contracts";
import { discoverDeploymentConfig, isDeploymentConfigured, type DeploymentConfig } from "./deployment";
import type { Redemption } from "./redemption";
import {
  baseWallet,
  connectInjectedWallet,
  ethereumWallet,
  friendlyError,
  switchToBase,
  switchToEthereum,
} from "./wallet";

type Status = { kind: "idle" | "pending" | "success" | "error"; message?: string; hash?: Hash; chain?: "base" | "ethereum" };
type ClaimRecord = Redemption & { transactionHash: Hash; blockNumber: bigint; processed: boolean };

const redemptionEvent = parseAbiItem(
  "event RedemptionInitiated(bytes32 indexed redemptionId, uint256 indexed redemptionNumber, address indexed burner, address recipient, uint256 burnAmount)",
);

export function AdminApp() {
  const [account, setAccount] = useState<Address>();
  const [collectionOwner, setCollectionOwner] = useState<Address>();
  const [burnerOwner, setBurnerOwner] = useState<Address>();
  const [receiverOwner, setReceiverOwner] = useState<Address>();
  const [burnerPaused, setBurnerPaused] = useState(false);
  const [receiverPaused, setReceiverPaused] = useState(false);
  const [mintedSupply, setMintedSupply] = useState<bigint>(0n);
  const [config, setConfig] = useState<DeploymentConfig>({});
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const [claims, setClaims] = useState<ClaimRecord[]>([]);
  const [loadingClaims, setLoadingClaims] = useState(false);

  const shortAccount = useMemo(
    () => account ? `${account.slice(0, 6)}…${account.slice(-4)}` : "Connect Ledger wallet",
    [account],
  );
  const ownerMatches = Boolean(
    account
    && collectionOwner
    && burnerOwner
    && receiverOwner
    && account.toLowerCase() === collectionOwner.toLowerCase()
    && account.toLowerCase() === burnerOwner.toLowerCase()
    && account.toLowerCase() === receiverOwner.toLowerCase(),
  );
  const configured = isDeploymentConfigured(config);

  useEffect(() => {
    refreshDeployment().catch((error) => setStatus({ kind: "error", message: friendlyError(error) }));
    if (!window.ethereum) return;
    window.ethereum.request({ method: "eth_accounts" }).then((accounts) => {
      const first = (accounts as string[])[0];
      if (first) setAccount(getAddress(first));
    });
  }, []);

  async function refreshDeployment() {
    const [nextConfig, nextCollectionOwner, nextSupply] = await Promise.all([
      discoverDeploymentConfig({ requireActivation: true }),
      ethereumClient.readContract({ address: HARDWARE_COLLECTION, abi: hardwareCollectionAbi, functionName: "owner" }),
      ethereumClient.readContract({ address: HARDWARE_COLLECTION, abi: hardwareCollectionAbi, functionName: "supply" }),
    ]);
    setConfig(nextConfig);
    setCollectionOwner(nextCollectionOwner);
    setMintedSupply(nextSupply);
    if (!nextConfig.receiverAddress) {
      setBurnerOwner(undefined);
      setReceiverOwner(undefined);
      return;
    }
    if (!nextConfig.burnerAddress) throw new Error("The active receiver has no validated Base burner.");
    const [nextBurnerOwner, nextReceiverOwner, nextBurnerPaused, nextReceiverPaused] = await Promise.all([
      baseClient.readContract({ address: nextConfig.burnerAddress, abi: burnerAbi, functionName: "owner" }),
      ethereumClient.readContract({ address: nextConfig.receiverAddress, abi: receiverAbi, functionName: "owner" }),
      baseClient.readContract({ address: nextConfig.burnerAddress, abi: burnerAbi, functionName: "paused" }),
      ethereumClient.readContract({ address: nextConfig.receiverAddress, abi: receiverAbi, functionName: "paused" }),
    ]);
    setBurnerOwner(nextBurnerOwner);
    setReceiverOwner(nextReceiverOwner);
    setBurnerPaused(nextBurnerPaused);
    setReceiverPaused(nextReceiverPaused);
  }

  async function connect() {
    try {
      const address = await connectInjectedWallet();
      setAccount(address);
      await refreshDeployment();
      setStatus({ kind: "success", message: "Ledger-backed owner connected. Verify every claim on Base before signing." });
      return address;
    } catch (error) {
      setStatus({ kind: "error", message: friendlyError(error) });
      throw error;
    }
  }

  async function requireOwner() {
    const walletAccount = account ?? await connect();
    const active = await discoverDeploymentConfig({ requireActivation: true });
    if (!active.receiverAddress || !active.burnerAddress) throw new Error("Complete the six-step deployment before operating claims.");
    const [nextCollectionOwner, nextReceiverOwner, nextBurnerOwner] = await Promise.all([
      ethereumClient.readContract({ address: HARDWARE_COLLECTION, abi: hardwareCollectionAbi, functionName: "owner" }),
      ethereumClient.readContract({ address: active.receiverAddress, abi: receiverAbi, functionName: "owner" }),
      baseClient.readContract({ address: active.burnerAddress, abi: burnerAbi, functionName: "owner" }),
    ]);
    if (
      walletAccount.toLowerCase() !== nextCollectionOwner.toLowerCase()
      || walletAccount.toLowerCase() !== nextReceiverOwner.toLowerCase()
      || walletAccount.toLowerCase() !== nextBurnerOwner.toLowerCase()
    ) throw new Error(`Connect the Hardware owner: ${nextCollectionOwner}.`);
    return { walletAccount, receiverAddress: active.receiverAddress, burnerAddress: active.burnerAddress };
  }

  async function setBurnPaused(paused: boolean) {
    try {
      const { walletAccount, burnerAddress } = await requireOwner();
      await switchToBase();
      setStatus({ kind: "pending", message: `${paused ? "Pausing" : "Unpausing"} Base burns. Review the transaction on your Ledger.`, chain: "base" });
      const hash = await baseWallet(walletAccount).writeContract({
        address: burnerAddress,
        abi: burnerAbi,
        functionName: paused ? "pause" : "unpause",
      });
      setStatus({ kind: "pending", message: "Waiting for Base confirmation…", hash, chain: "base" });
      await baseClient.waitForTransactionReceipt({ hash });
      setBurnerPaused(paused);
      setStatus({ kind: "success", message: `New Base burns ${paused ? "paused" : "unpaused"}.`, hash, chain: "base" });
    } catch (error) {
      setStatus({ kind: "error", message: friendlyError(error) });
    }
  }

  async function setPaused(paused: boolean) {
    try {
      const { walletAccount, receiverAddress } = await requireOwner();
      await switchToEthereum();
      setStatus({ kind: "pending", message: `${paused ? "Pausing" : "Unpausing"} Ledger claim approvals. Review the transaction on your Ledger.`, chain: "ethereum" });
      const hash = await ethereumWallet(walletAccount).writeContract({
        address: receiverAddress,
        abi: receiverAbi,
        functionName: paused ? "pause" : "unpause",
      });
      setStatus({ kind: "pending", message: "Waiting for Ethereum confirmation…", hash, chain: "ethereum" });
      await ethereumClient.waitForTransactionReceipt({ hash });
      setReceiverPaused(paused);
      setStatus({ kind: "success", message: `Ledger claim approvals ${paused ? "paused" : "unpaused"}.`, hash, chain: "ethereum" });
    } catch (error) {
      setStatus({ kind: "error", message: friendlyError(error) });
    }
  }

  async function loadClaims() {
    if (!config.burnerAddress || !config.receiverAddress) {
      setStatus({ kind: "error", message: "Complete the six-step deployment before loading claims." });
      return;
    }
    try {
      setLoadingClaims(true);
      setStatus({ kind: "pending", message: "Reading every Base burn and its Ethereum mint state…" });
      const fromBlock = config.burnerDeploymentBlock
        ? BigInt(config.burnerDeploymentBlock)
        : await baseClient.readContract({ address: config.burnerAddress, abi: burnerAbi, functionName: "deploymentBlock" });
      const [finalizedBlock, currentSupply] = await Promise.all([
        baseClient.getBlock({ blockTag: "finalized" }),
        ethereumClient.readContract({ address: HARDWARE_COLLECTION, abi: hardwareCollectionAbi, functionName: "supply" }),
      ]);
      const logs = await baseClient.getLogs({
        address: config.burnerAddress,
        event: redemptionEvent,
        fromBlock,
        toBlock: finalizedBlock.number,
      });
      const ordered = [...logs].sort((left, right) => Number(left.args.redemptionNumber! - right.args.redemptionNumber!));
      const records = await Promise.all(ordered.map(async (log) => {
        const args = log.args;
        const processed = await ethereumClient.readContract({
          address: config.receiverAddress!,
          abi: receiverAbi,
          functionName: "processed",
          args: [args.redemptionId!],
        });
        return {
          sourceChainId: 8453n,
          sourceContract: config.burnerAddress!,
          redemptionId: args.redemptionId!,
          redemptionNumber: args.redemptionNumber!,
          burner: args.burner!,
          recipient: args.recipient!,
          burnAmount: args.burnAmount!,
          transactionHash: log.transactionHash!,
          blockNumber: log.blockNumber!,
          processed,
        } satisfies ClaimRecord;
      }));
      setClaims(records);
      setMintedSupply(currentSupply);
      setStatus({ kind: "success", message: `${records.length} finalized redemption${records.length === 1 ? "" : "s"} loaded in mint order.` });
    } catch (error) {
      setStatus({ kind: "error", message: friendlyError(error) });
    } finally {
      setLoadingClaims(false);
    }
  }

  async function approveClaim(claim: ClaimRecord) {
    try {
      const { walletAccount, receiverAddress } = await requireOwner();
      const currentSupply = await ethereumClient.readContract({ address: HARDWARE_COLLECTION, abi: hardwareCollectionAbi, functionName: "supply" });
      if (claim.redemptionNumber !== currentSupply + 1n) {
        throw new Error(`Hardware #${(currentSupply + 1n).toString()} must mint next.`);
      }
      await switchToEthereum();
      setStatus({ kind: "pending", message: `Minting Hardware #${claim.redemptionNumber.toString()}. Verify the Base transaction and recipient on your Ledger.`, chain: "ethereum" });
      const hash = await ethereumWallet(walletAccount).writeContract({
        address: receiverAddress,
        abi: receiverAbi,
        functionName: "ownerFinalize",
        args: [stripClaimRecord(claim)],
      });
      setStatus({ kind: "pending", message: "Waiting for the Ethereum mint…", hash, chain: "ethereum" });
      await ethereumClient.waitForTransactionReceipt({ hash });
      setStatus({ kind: "success", message: `Hardware #${claim.redemptionNumber.toString()} minted.`, hash, chain: "ethereum" });
      await loadClaims();
    } catch (error) {
      setStatus({ kind: "error", message: friendlyError(error) });
    }
  }

  return (
    <main className="admin-shell">
      <header className="admin-header">
        <div>
          <a className="back-link" href="/">← Hardware claim site</a>
          <a className="admin-deploy-link" href="/deploy">Six-step deployment ↗</a>
          <p className="eyebrow">Owner console</p>
          <h1>Operate Hardware.</h1>
          <p className="admin-intro">This console can only approve finalized Base burns with the same Ledger owner that controls the Hardware collection. There is no alternate deploy, relay, or hot-key path here.</p>
        </div>
        <button className="wallet admin-wallet" onClick={connect}>{shortAccount}</button>
      </header>

      {status.message && (
        <div className={`admin-status transaction ${status.kind}`}>
          {status.message}
          {status.hash && <a href={`${status.chain === "base" ? "https://basescan.org" : "https://etherscan.io"}/tx/${status.hash}`} target="_blank" rel="noreferrer">View transaction ↗</a>}
        </div>
      )}

      <section className="admin-grid">
        <article className="admin-card">
          <span className="step">Deployment</span>
          <h2>{configured ? "Independent contracts active" : "Deployment incomplete"}</h2>
          <AddressLine label="Base burner" value={config.burnerAddress} explorer="https://basescan.org/address/" />
          <AddressLine label="Ethereum receiver" value={config.receiverAddress} explorer="https://etherscan.io/address/" />
          <p className="card-note">Owner: {collectionOwner ?? "Reading chain…"}</p>
          <p className="card-note">Connected owner: {ownerMatches ? "Verified" : "Required"}</p>
          {!configured && <a className="secondary button-link" href="/deploy">Complete deployment</a>}
        </article>

        <article className="admin-card">
          <span className="step">Safety</span>
          <h2>Base burn control</h2>
          <p className="card-note">Pause this first in an emergency so collectors cannot initiate new burns. Existing finalized claims remain intact.</p>
          <div className="button-row">
            <button className="secondary" onClick={() => setBurnPaused(true)} disabled={!configured || !ownerMatches || burnerPaused}>Pause burns</button>
            <button className="secondary" onClick={() => setBurnPaused(false)} disabled={!configured || !ownerMatches || !burnerPaused}>Unpause burns</button>
          </div>
          <p className="address-line"><span>Status</span><strong>{!configured ? "Unavailable" : burnerPaused ? "Paused" : "Open"}</strong></p>
        </article>

        <article className="admin-card">
          <span className="step">Safety</span>
          <h2>Ethereum mint control</h2>
          <p className="card-note">Pausing stops every normal and historical mint through the receiver. Unpausing requires the same Ledger owner.</p>
          <div className="button-row">
            <button className="secondary" onClick={() => setPaused(true)} disabled={!configured || !ownerMatches || receiverPaused}>Pause</button>
            <button className="secondary" onClick={() => setPaused(false)} disabled={!configured || !ownerMatches || !receiverPaused}>Unpause</button>
          </div>
          <p className="address-line"><span>Status</span><strong>{!configured ? "Unavailable" : receiverPaused ? "Paused" : "Open"}</strong></p>
        </article>
      </section>

      <section className="claim-queue">
        <div className="queue-heading">
          <div>
            <p className="eyebrow">Ledger claim queue</p>
            <h2>Finalized Base burns · oldest first</h2>
          </div>
          <button className="secondary" onClick={loadClaims} disabled={!configured || loadingClaims}>{loadingClaims ? "Loading…" : "Load claims"}</button>
        </div>
        <div className="queue-list">
          {claims.length === 0 && <p className="empty-queue">No claims loaded.</p>}
          {claims.map((claim) => {
            const nextToken = mintedSupply + 1n;
            const canApprove = ownerMatches && !receiverPaused && !claim.processed && claim.redemptionNumber === nextToken;
            return (
              <article className="queue-item" key={claim.redemptionId}>
                <div><span>Redemption</span><strong>#{claim.redemptionNumber.toString()}</strong></div>
                <div><span>Recipient</span><strong>{shorten(claim.recipient)}</strong></div>
                <div><span>Burned</span><strong>{claim.burnAmount.toString()} × 1988</strong></div>
                <a href={`https://basescan.org/tx/${claim.transactionHash}`} target="_blank" rel="noreferrer">Verify on Base ↗</a>
                {claim.processed
                  ? <span className="minted">Minted</span>
                  : <button className="primary queue-action" onClick={() => approveClaim(claim)} disabled={!canApprove}>
                      {claim.redemptionNumber === nextToken ? "Approve on Ledger" : `Mint #${nextToken.toString()} first`}
                    </button>}
              </article>
            );
          })}
        </div>
      </section>
    </main>
  );
}

function stripClaimRecord(claim: ClaimRecord): Redemption {
  return {
    sourceChainId: claim.sourceChainId,
    sourceContract: claim.sourceContract,
    redemptionId: claim.redemptionId,
    redemptionNumber: claim.redemptionNumber,
    burner: claim.burner,
    recipient: claim.recipient,
    burnAmount: claim.burnAmount,
  };
}

function shorten(value: string) {
  return `${value.slice(0, 8)}…${value.slice(-6)}`;
}

function AddressLine({ label, value, explorer }: { label: string; value?: string; explorer: string }) {
  return (
    <p className="address-line">
      <span>{label}</span>
      {value ? <a href={`${explorer}${value}`} target="_blank" rel="noreferrer">{shorten(value)} ↗</a> : <strong>Not deployed</strong>}
    </p>
  );
}
